import React, { Children, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { get, noop, sortBy } from 'lodash'

import { Box, LinearProgress, Pagination } from '@mui/material'

import { CUSTOMER_DETAILS_DEFAULT_PAGINATION } from 'settings/constants/pagination'

import T from 'T'
import HHTablePagination from 'components/common/HHTablePagination'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import DraftServiceAccordionItem from 'components/customer-details/content/configured-services/draft/DraftServiceAccordionItem'
import ServiceAccordionItem from './ServiceAccordionItem'
import ServiceSkeleton from './ServiceSkeleton'
import { CONFIGURED_SERVICE_STATUS } from './settings'

const { INITIAL_PAGE, ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } = CUSTOMER_DETAILS_DEFAULT_PAGINATION

const ConfiguredServiceList = ({
  isFetching,
  isLoading,
  selectedServiceId,
  services = [],
  withDeactivated = false,
  noRowsText = T.NO_CONFIGURED_SERVICES,
  canEdit = false,
  onEditService = noop,
  showAddEventButton = false,
  onAddEventClick = noop,
  locationId = '',
  accountId = '',
}) => {
  const [pagination, setPagination] = useState({ page: INITIAL_PAGE, rowsPerPage: ROWS_PER_PAGE })
  const { page, rowsPerPage } = pagination

  const sortedServices = useMemo(() => sortBy(services, 'status'), [services])
  const filteredServices = useMemo(
    () => (withDeactivated ? sortedServices : sortedServices.filter(service => service.status === CONFIGURED_SERVICE_STATUS.ACTIVE)),
    [sortedServices, withDeactivated]
  )
  const totalCount = useMemo(() => filteredServices.length, [filteredServices])
  const totalPages = useMemo(() => Math.ceil(totalCount / rowsPerPage), [totalCount, rowsPerPage])
  const hasServices = useMemo(() => totalCount > 0, [totalCount])
  const currentPageServices = filteredServices.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
  const showPagination = useMemo(() => totalCount > ROWS_PER_PAGE, [totalCount])

  const handlePageChange = (newPage = INITIAL_PAGE) => setPagination(p => ({ ...p, page: newPage }))

  const handleRowsPerPageChange = event => setPagination({ page: INITIAL_PAGE, rowsPerPage: event.target.value })

  useEffect(() => {
    if (hasServices && !currentPageServices?.length) {
      handlePageChange()
    }
  }, [hasServices, currentPageServices])

  return (
    <>
      {isFetching && <LinearProgress />}

      {isLoading && <ServiceSkeleton rows={2} />}

      {!isLoading && !hasServices && <HHSectionPlaceholder title={noRowsText} />}

      {!isLoading &&
        hasServices &&
        Children.toArray(
          currentPageServices.map(service => {
            const isDraft = get(service, 'isDraft', false)
            return (
              <>
                {isDraft && <DraftServiceAccordionItem service={service} locationId={locationId} accountId={accountId} />}
                {!isDraft && (
                  <ServiceAccordionItem
                    key={service.configuredServiceId}
                    service={service}
                    showAddEventButton={showAddEventButton}
                    onAddEventClick={onAddEventClick}
                    selectedServiceId={selectedServiceId}
                    canEdit={canEdit}
                    locationId={locationId}
                    onClick={event => onEditService(event, service)}
                  />
                )}
              </>
            )
          })
        )}

      {showPagination && (
        <Box backgroundColor="background.header" px={3} display="flex" alignItems="center" justifyContent="space-between">
          <HHTablePagination
            page={page}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            onPageChange={(event, newPage) => handlePageChange(newPage)}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onRowsPerPageChange={handleRowsPerPageChange}
            ActionsComponent={params => <Box display="none" {...params} />}
          />
          <Pagination color="primary" count={totalPages} page={page + 1} onChange={(event, newPage) => handlePageChange(newPage - 1)} />
        </Box>
      )}
    </>
  )
}

ConfiguredServiceList.propTypes = {
  isFetching: PropTypes.bool,
  isLoading: PropTypes.bool,
  selectedServiceId: PropTypes.string,
  services: PropTypes.array,
  withDeactivated: PropTypes.bool,
  noRowsText: PropTypes.string,
  canEdit: PropTypes.bool,
  onEditService: PropTypes.func,
  showAddEventButton: PropTypes.bool,
  onAddEventClick: PropTypes.func,
  locationId: PropTypes.string,
  accountId: PropTypes.string,
}

export default ConfiguredServiceList
